import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useSound } from '../contexts/SoundContext';
import PageLayout from '../components/PageLayout';
import SkinCard from '../components/SkinCard';

const LegendarySkins = () => {
  const { t } = useLanguage();
  const { playButtonClick } = useSound();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [skins, setSkins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedSkinIndex, setSelectedSkinIndex] = useState(null); // Selected skin index for modal
  const [likeCounts, setLikeCounts] = useState({}); // Store like counts for all skins
  const [likedSkins, setLikedSkins] = useState(new Set()); // Store liked skin names from localStorage
  const [likingSkin, setLikingSkin] = useState(null); // Track which skin is currently being liked/unliked
  const [sortOption, setSortOption] = useState(searchParams.get('sort') || 'likes-desc'); // Default sort by likes (high to low)
  const [shareCopied, setShareCopied] = useState(false); // Track if share link was copied
  const [debouncedLikeCounts, setDebouncedLikeCounts] = useState({}); // Debounced version for sorting
  const likeDebounceRef = useRef(null);

  // Load liked skins from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('legendary_skins_likes');
        if (stored) {
          const likedArray = JSON.parse(stored);
          setLikedSkins(new Set(likedArray));
        }
      } catch (err) {
        console.error('Failed to load liked skins from localStorage:', err);
      }
    }
  }, []);

  // Load skins data and like counts
  useEffect(() => {
    const loadSkins = async () => {
      try {
        setLoading(true);
        // Use AbortController for timeout handling
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const [skinsResponse, likesResponse] = await Promise.all([
          fetch('/legendary_skins.json', { signal: controller.signal }),
          fetch('/get-skin-likes.php', { signal: controller.signal })
        ]);
        clearTimeout(timeoutId);
        
        if (!skinsResponse.ok) {
          throw new Error('Failed to load skins');
        }
        const skinsData = await skinsResponse.json();
        setSkins(skinsData);

        // Load like counts
        if (likesResponse.ok) {
          const likesData = await likesResponse.json();
          if (likesData.likes) {
            setLikeCounts(likesData.likes);
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          setError('Request timeout. Please refresh the page.');
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    // Only load if we're in a browser environment (not during SSR)
    if (typeof window !== 'undefined') {
      loadSkins();
    } else {
      setLoading(false);
    }
  }, []);

  // Debounce likeCounts updates for sorting (prevents re-sort on every rapid like)
  useEffect(() => {
    if (likeDebounceRef.current) clearTimeout(likeDebounceRef.current);
    likeDebounceRef.current = setTimeout(() => {
      setDebouncedLikeCounts(likeCounts);
    }, 500);
    return () => clearTimeout(likeDebounceRef.current);
  }, [likeCounts]);

  // Memoize filtered and sorted skins - uses debouncedLikeCounts for sorting
  const filteredSkins = useMemo(() => {
    // First, filter by search term
    let filtered = skins;
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = skins.filter(skin => {
        const skinName = (skin['Skin name'] || '').toLowerCase();
        const mechName = (skin['Mech name'] || '').toLowerCase();
        return skinName.includes(searchLower) || mechName.includes(searchLower);
      });
    }

    // Then, sort the filtered results (uses debouncedLikeCounts to avoid re-sort on every like)
    const sorted = [...filtered].sort((a, b) => {
      if (sortOption.startsWith('likes')) {
        const countA = debouncedLikeCounts[a['Skin name']] || 0;
        const countB = debouncedLikeCounts[b['Skin name']] || 0;
        return sortOption === 'likes-desc' ? countB - countA : countA - countB;
      } else if (sortOption.startsWith('skin-name')) {
        const nameA = (a['Skin name'] || '').toLowerCase();
        const nameB = (b['Skin name'] || '').toLowerCase();
        const comparison = nameA.localeCompare(nameB);
        return sortOption === 'skin-name-desc' ? -comparison : comparison;
      } else if (sortOption.startsWith('mech-name')) {
        const nameA = (a['Mech name'] || '').toLowerCase();
        const nameB = (b['Mech name'] || '').toLowerCase();
        const comparison = nameA.localeCompare(nameB);
        return sortOption === 'mech-name-desc' ? -comparison : comparison;
      }
      return 0;
    });

    return sorted;
  }, [skins, searchTerm, sortOption, debouncedLikeCounts]);

  // Image path helpers - prefer WebP with PNG fallback
  const getImagePath = useCallback((skinName) => {
    const encodedName = encodeURIComponent(skinName);
    return `/Legendary skins/${encodedName}.webp`;
  }, []);

  const getThumbnailPath = useCallback((skinName) => {
    const encodedName = encodeURIComponent(skinName);
    return `/Legendary skins/thumbnails/${encodedName}.webp`;
  }, []);

  const getFallbackPath = useCallback((skinName) => {
    const encodedName = encodeURIComponent(skinName);
    return `/Legendary skins/${encodedName}.png`;
  }, []);

  // Handle image error - fallback from WebP to PNG
  const handleImageError = useCallback((e) => {
    const src = e.target.src;
    if (src.endsWith('.webp')) {
      // Try PNG fallback
      e.target.src = src.replace('/thumbnails/', '/').replace('.webp', '.png');
    } else {
      e.target.style.display = 'none';
    }
  }, []);

  // Set document title
  useEffect(() => {
    document.title = `${t('legendarySkins.title')} - ${t('app.title')}`;
  }, [t, location]);

  const handleOpenModal = useCallback((skin, index) => {
    setSelectedSkinIndex(index);
    playButtonClick();
  }, [playButtonClick]);

  const handleCloseModal = useCallback(() => {
    setSelectedSkinIndex(null);
    playButtonClick();
  }, [playButtonClick]);

  const handleNextSkin = useCallback(() => {
    if (selectedSkinIndex !== null && selectedSkinIndex < filteredSkins.length - 1) {
      setSelectedSkinIndex(selectedSkinIndex + 1);
      playButtonClick();
    }
  }, [selectedSkinIndex, filteredSkins.length, playButtonClick]);

  const handlePreviousSkin = useCallback(() => {
    if (selectedSkinIndex !== null && selectedSkinIndex > 0) {
      setSelectedSkinIndex(selectedSkinIndex - 1);
      playButtonClick();
    }
  }, [selectedSkinIndex, playButtonClick]);

  // Handle keyboard navigation (ESC, Arrow keys)
  useEffect(() => {
    if (selectedSkinIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCloseModal();
      } else if (e.key === 'ArrowRight' && selectedSkinIndex < filteredSkins.length - 1) {
        handleNextSkin();
      } else if (e.key === 'ArrowLeft' && selectedSkinIndex > 0) {
        handlePreviousSkin();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden'; // Prevent body scroll when modal is open

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [selectedSkinIndex, filteredSkins.length, handleCloseModal, handleNextSkin, handlePreviousSkin]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Update URL parameter
    const newSearchParams = new URLSearchParams(searchParams);
    if (value.trim()) {
      newSearchParams.set('search', value);
    } else {
      newSearchParams.delete('search');
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    playButtonClick();
    // Remove search from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('search');
    setSearchParams(newSearchParams, { replace: true });
  }, [playButtonClick, searchParams, setSearchParams]);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    playButtonClick();
  }, [playButtonClick]);

  // Handle sort change
  const handleSortChange = useCallback((e) => {
    const value = e.target.value;
    setSortOption(value);
    playButtonClick();
    // Update URL parameter
    const newSearchParams = new URLSearchParams(searchParams);
    if (value && value !== 'likes-desc') {
      newSearchParams.set('sort', value);
    } else {
      newSearchParams.delete('sort');
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [playButtonClick, searchParams, setSearchParams]);

  // Handle share link copy
  const handleShareLink = useCallback(async () => {
    const currentUrl = window.location.href;
    
    // Check if clipboard API is available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(currentUrl);
        setShareCopied(true);
        playButtonClick();
        // Reset copied state after 2 seconds
        setTimeout(() => {
          setShareCopied(false);
        }, 2000);
        return;
      } catch (err) {
        console.error('Clipboard API failed, trying fallback:', err);
      }
    }
    
    // Fallback for browsers without clipboard API or secure context
    try {
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setShareCopied(true);
        playButtonClick();
        setTimeout(() => {
          setShareCopied(false);
        }, 2000);
      } else {
        console.error('Fallback copy command failed');
        // Show error to user (optional)
        alert(t('legendarySkins.copyFailed') || 'Failed to copy link. Please copy manually from the address bar.');
      }
    } catch (fallbackErr) {
      console.error('Fallback copy failed:', fallbackErr);
      alert(t('legendarySkins.copyFailed') || 'Failed to copy link. Please copy manually from the address bar.');
    }
  }, [playButtonClick, t]);

  // Handle like/unlike toggle
  const handleToggleLike = useCallback(async (skinName) => {
    if (!skinName || likingSkin === skinName) return; // Prevent duplicate requests

    const isCurrentlyLiked = likedSkins.has(skinName);
    const action = isCurrentlyLiked ? 'unlike' : 'like';

    // Optimistic update
    setLikingSkin(skinName);
    const previousCount = likeCounts[skinName] || 0;
    const previousLikedSkins = new Set(likedSkins);
    const previousLikeCounts = { ...likeCounts };

    // Update UI immediately
    const newLikeCount = Math.max(0, previousCount + (isCurrentlyLiked ? -1 : 1));
    setLikeCounts(prev => ({
      ...prev,
      [skinName]: newLikeCount
    }));

    // Update liked skins state
    const newLikedSkins = new Set(likedSkins);
    if (isCurrentlyLiked) {
      newLikedSkins.delete(skinName);
    } else {
      newLikedSkins.add(skinName);
    }
    setLikedSkins(newLikedSkins);

    // Update localStorage
    try {
      localStorage.setItem('legendary_skins_likes', JSON.stringify(Array.from(newLikedSkins)));
    } catch (err) {
      console.error('Failed to update localStorage:', err);
    }

    // Call API
    try {
      const response = await fetch('/toggle-skin-like.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          skin_name: skinName,
          action: action
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update like');
      }

      const data = await response.json();
      if (data.success) {
        // Update with server response
        setLikeCounts(prev => ({
          ...prev,
          [skinName]: data.count
        }));
      } else {
        throw new Error(data.error || 'Failed to update like');
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
      // Rollback optimistic update
      setLikeCounts(previousLikeCounts);
      setLikedSkins(previousLikedSkins);
      // Show error message (optional)
      alert(t('legendarySkins.likeError'));
    } finally {
      setLikingSkin(null);
    }
  }, [likedSkins, likeCounts, likingSkin, t]);

  // Memoize selected skin data for modal
  const selectedSkin = useMemo(() => {
    if (selectedSkinIndex === null || !filteredSkins[selectedSkinIndex]) {
      return null;
    }
    return filteredSkins[selectedSkinIndex];
  }, [selectedSkinIndex, filteredSkins]);

  // Memoize selected skin image path (full-size WebP for modal)
  const selectedSkinImagePath = useMemo(() => {
    if (!selectedSkin) return null;
    return getImagePath(selectedSkin['Skin name'] || '');
  }, [selectedSkin, getImagePath]);

  // Fallback path for modal image
  const selectedSkinFallbackPath = useMemo(() => {
    if (!selectedSkin) return null;
    return getFallbackPath(selectedSkin['Skin name'] || '');
  }, [selectedSkin, getFallbackPath]);

  // Memoize selected skin like state
  const selectedSkinLikeState = useMemo(() => {
    if (!selectedSkin) return null;
    const skinName = selectedSkin['Skin name'] || '';
    return {
      skinName,
      isLiked: likedSkins.has(skinName),
      likeCount: likeCounts[skinName] || 0,
      isLiking: likingSkin === skinName
    };
  }, [selectedSkin, likedSkins, likeCounts, likingSkin]);

  // Preload next and previous images for smoother modal navigation
  useEffect(() => {
    if (selectedSkinIndex === null) return;

    const controller = new AbortController();
    const preloadUrls = [];

    // Collect URLs to preload
    if (selectedSkinIndex > 0) {
      const prevSkin = filteredSkins[selectedSkinIndex - 1];
      if (prevSkin) preloadUrls.push(getImagePath(prevSkin['Skin name'] || ''));
    }
    if (selectedSkinIndex < filteredSkins.length - 1) {
      const nextSkin = filteredSkins[selectedSkinIndex + 1];
      if (nextSkin) preloadUrls.push(getImagePath(nextSkin['Skin name'] || ''));
    }

    // Use fetch with AbortController for proper cancellation
    preloadUrls.forEach(url => {
      fetch(url, { signal: controller.signal }).catch(() => {});
    });

    return () => controller.abort();
  }, [selectedSkinIndex, filteredSkins, getImagePath]);

  return (
    <PageLayout 
      title={t('legendarySkins.title')}
      subtitle={t('legendarySkins.subtitle')}
      showParticles={false}
    >
      {/* Submit Skin Form Section */}
      <div className="mb-6 sm:mb-8">
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-4 sm:p-6 border border-indigo-200 dark:border-indigo-700 shadow-md">
          <div className="text-center">
            <h4 className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-200 mb-3">
              {t('legendarySkins.submitSkinTitle')}
            </h4>
            <a 
              href="https://forms.gle/9G6WdewyzUjfsRkT8" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof gtag !== 'undefined') {
                  gtag('event', 'external_link_click', {
                    'link_destination': 'submit_skin_form',
                    'link_location': 'legendary_skins_page'
                  });
                }
              }}

              className="inline-flex items-center space-x-2 bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-500 dark:to-purple-500 hover:from-indigo-700 hover:to-purple-700 dark:hover:from-indigo-600 dark:hover:to-purple-600 text-white px-5 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer group"
            >
              <svg className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>{t('legendarySkins.submitSkinButton')}</span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10 border border-gray-200 dark:border-gray-600">
        {/* Search and View Toggle Controls */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
          {/* Search Bar and Sort Control */}
          <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full sm:max-w-2xl">
            {/* Search Bar */}
            <div className="relative flex-1 w-full sm:max-w-md flex items-center gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={t('legendarySkins.searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
    
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:border-indigo-500 dark:focus:border-orange-glow"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
      
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Clear search"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {/* Share Button */}
              <div className="relative">
                <button
                  onClick={handleShareLink}
    
                  className={`flex items-center justify-center w-10 h-10 px-3 py-2 rounded-lg border transition-all duration-200 ${
                    shareCopied
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-600 dark:text-green-400'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-indigo-300 dark:hover:border-orange-500'
                  }`}
                  aria-label={shareCopied ? t('legendarySkins.linkCopied') : t('legendarySkins.shareLink')}
                  title={shareCopied ? t('legendarySkins.linkCopied') : t('legendarySkins.shareLink')}
                >
                  {shareCopied ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  )}
                </button>
                {/* Toast Notification */}
                {shareCopied && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-green-600 dark:bg-green-500 text-white text-sm rounded-lg shadow-lg whitespace-nowrap z-50 animate-in slide-in-from-top-2 pointer-events-none">
                    {t('legendarySkins.linkCopied')}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-green-600 dark:bg-green-500 rotate-45"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Sort Control */}
            <div className="flex items-center gap-2">
              <label htmlFor="sort-select" className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                {t('legendarySkins.sortBy')}
              </label>
              <select
                id="sort-select"
                value={sortOption}
                onChange={handleSortChange}
  
                className="block px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:border-indigo-500 dark:focus:border-orange-glow text-sm"
              >
                <option value="likes-desc">{t('legendarySkins.sortLikesDesc')}</option>
                <option value="likes-asc">{t('legendarySkins.sortLikesAsc')}</option>
                <option value="skin-name-asc">{t('legendarySkins.sortSkinNameAsc')}</option>
                <option value="skin-name-desc">{t('legendarySkins.sortSkinNameDesc')}</option>
                <option value="mech-name-asc">{t('legendarySkins.sortMechNameAsc')}</option>
                <option value="mech-name-desc">{t('legendarySkins.sortMechNameDesc')}</option>
              </select>
            </div>
          </div>

          {/* View Toggle Buttons */}
          <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => handleViewModeChange('grid')}

              className={`p-2 rounded-md transition-all duration-200 ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-[#FF9800] shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              aria-label={t('legendarySkins.gridView')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => handleViewModeChange('list')}

              className={`p-2 rounded-md transition-all duration-200 ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-[#FF9800] shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              aria-label={t('legendarySkins.listView')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Results Count */}
        {!loading && !error && (
          <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            {filteredSkins.length} {filteredSkins.length === 1 ? 'skin' : 'skins'} {searchTerm && `found`}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-[#FF9800] mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">{t('legendarySkins.loading')}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded">
              <strong className="font-bold">{t('legendarySkins.error')}</strong>
              <span className="block sm:inline"> {error}</span>
            </div>
          </div>
        )}

        {/* No Results */}
        {!loading && !error && filteredSkins.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-600 dark:text-gray-300 text-lg">{t('legendarySkins.noResults')}</p>
          </div>
        )}

        {/* Grid View */}
        {!loading && !error && filteredSkins.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {filteredSkins.map((skin, index) => {
              const skinName = skin['Skin name'] || '';
              return (
                <SkinCard
                  key={skinName || index}
                  skin={skin}
                  index={index}
                  viewMode="grid"
                  imagePath={getThumbnailPath(skinName)}
                  onOpenModal={handleOpenModal}
                  onImageError={handleImageError}
                  isLiked={likedSkins.has(skinName)}
                  likeCount={likeCounts[skinName] || 0}
                  onToggleLike={handleToggleLike}
                  isLiking={likingSkin === skinName}
                />
              );
            })}
          </div>
        )}

        {/* List View */}
        {!loading && !error && filteredSkins.length > 0 && viewMode === 'list' && (
          <div className="space-y-2">
            {filteredSkins.map((skin, index) => {
              const skinName = skin['Skin name'] || '';
              return (
                <SkinCard
                  key={skinName || index}
                  skin={skin}
                  index={index}
                  viewMode="list"
                  imagePath={getThumbnailPath(skinName)}
                  onOpenModal={handleOpenModal}
                  onImageError={handleImageError}
                  isLiked={likedSkins.has(skinName)}
                  likeCount={likeCounts[skinName] || 0}
                  onToggleLike={handleToggleLike}
                  isLiking={likingSkin === skinName}
                />
              );
            })}
          </div>
        )}

        {/* Skin Detail Modal - Rendered via Portal */}
        {selectedSkin && typeof window !== 'undefined' && createPortal(
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-75 dark:bg-black dark:bg-opacity-80"
            onClick={handleCloseModal}
          >
            {/* Modal panel - centered in viewport */}
            <div 
              className="relative bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4 relative">
                  {/* Close button */}
                  <button
                    onClick={handleCloseModal}
      
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors z-10 bg-white dark:bg-gray-800 rounded-full p-1 shadow-md"
                    aria-label="Close modal"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Previous button */}
                  {selectedSkinIndex > 0 && (
                    <button
                      onClick={handlePreviousSkin}
        
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors z-10 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      aria-label="Previous skin"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}

                  {/* Next button */}
                  {selectedSkinIndex < filteredSkins.length - 1 && (
                    <button
                      onClick={handleNextSkin}
        
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors z-10 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      aria-label="Next skin"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}

                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      {/* Skin Info */}
                      <div className="mb-4 pr-10">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                          {selectedSkin['Skin name']}
                        </h3>
                        <p className="text-lg text-gray-600 dark:text-gray-400">
                          <span className="font-medium">{t('legendarySkins.mechName')}:</span>{' '}
                          <span className="text-indigo-600 dark:text-[#FF9800]">{selectedSkin['Mech name']}</span>
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                          {selectedSkinIndex + 1} / {filteredSkins.length}
                        </p>
                        {/* Like Button in Modal */}
                        {selectedSkinLikeState && (
                          <div className="mt-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleLike(selectedSkinLikeState.skinName);
                                playButtonClick();
                              }}
                
                              disabled={selectedSkinLikeState.isLiking}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                                selectedSkinLikeState.isLiked
                                  ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                                  : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-500'
                              } ${selectedSkinLikeState.isLiking ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                              aria-label={selectedSkinLikeState.isLiked ? t('legendarySkins.unlike') : t('legendarySkins.like')}
                            >
                              <svg 
                                className={`w-6 h-6 transition-transform duration-200 ${selectedSkinLikeState.isLiking ? 'animate-pulse' : ''}`}
                                fill={selectedSkinLikeState.isLiked ? 'currentColor' : 'none'}
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                              </svg>
                              <span className="font-medium">
                                {selectedSkinLikeState.likeCount > 0 
                                  ? `${selectedSkinLikeState.likeCount} ${selectedSkinLikeState.likeCount === 1 ? t('legendarySkins.like') : t('legendarySkins.likes')}` 
                                  : t('legendarySkins.like')}
                              </span>
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Large Image */}
                      <div className="text-center">
                        <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4 mb-4 relative min-h-[200px] flex items-center justify-center">
                          {/* Skeleton placeholder */}
                          {selectedSkinImagePath && (
                            <img 
                              src={selectedSkinImagePath} 
                              alt={selectedSkin['Skin name']}
                              className="max-w-full h-auto max-h-[70vh] mx-auto rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 object-contain"
                              onError={handleImageError}
                              loading="eager"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          </div>,
          document.body
        )}
      </div>
    </PageLayout>
  );
};

export default LegendarySkins;


